import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toast';
import { LuCalendarClock, LuCircleAlert, LuLoaderCircle, LuPlus, LuRefreshCw } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import { useDebounce } from '../../hooks/useDebounce';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import ScheduleTestPlanModal from '../test-plans/ScheduleTestPlanModal';
import {
  Breadcrumbs,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  MetricCard,
  PageScaffold,
  Pagination,
  RowActions,
  SearchInput,
  Select,
} from '../ui';
import { formatDateTime as formatUserDateTime } from '../../utils/dateTimeFormat';

const SCHEDULE_TYPES = ['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'];
const SCHEDULE_STATUSES = ['SCHEDULED', 'PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED'];
const QUEUE_STATUSES = ['QUEUED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];

const STATUS_TONE = {
  SCHEDULED: 'info',
  PENDING: 'warning',
  RUNNING: 'info',
  COMPLETED: 'success',
  FAILED: 'danger',
  CANCELLED: 'muted',
};
const QUEUE_TONE = { QUEUED: 'muted', IN_PROGRESS: 'info', COMPLETED: 'success', FAILED: 'danger' };

const PAGE_SIZES = [10, 20, 50];
const MIN_SEARCH_LENGTH = 2;
const FILTER_KEYS = ['s_q', 's_type', 's_status', 's_queue'];

const humanize = (value) => {
  const s = String(value || '').replace(/_/g, ' ').toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
};

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = toDate(value);
  return d ? formatUserDateTime(d) : '—';
};

const RELATIVE_UNITS = [
  ['year', 365 * 24 * 3600e3],
  ['month', 30 * 24 * 3600e3],
  ['day', 24 * 3600e3],
  ['hour', 3600e3],
  ['minute', 60e3],
];
const relativeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const formatRelative = (value) => {
  const d = toDate(value);
  if (!d) return '';
  const diff = d.getTime() - Date.now();
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(diff) >= ms) return relativeFormatter.format(Math.round(diff / ms), unit);
  }
  return diff >= 0 ? 'in under a minute' : 'just now';
};

const Badge = ({ tone = 'muted', children }) => (
  <span className={`rt-badge rt-badge--${tone}`}>{children}</span>
);

/* ------------------------------------------------------------------ */
/* State + data                                                         */
/* ------------------------------------------------------------------ */

function useSchedules() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = searchParams.get('project_id') || getProjectIdFromURL() || selectedProject?.id || null;

  const page = Math.max(1, parseInt(searchParams.get('s_page'), 10) || 1);
  const sizeParam = parseInt(searchParams.get('s_size'), 10);
  const pageSize = PAGE_SIZES.includes(sizeParam) ? sizeParam : PAGE_SIZES[0];
  const filters = {
    q: searchParams.get('s_q') || '',
    type: searchParams.get('s_type') || '',
    status: searchParams.get('s_status') || '',
    queue: searchParams.get('s_queue') || '',
  };
  const filtersActive = FILTER_KEYS.some((k) => searchParams.get(k));

  const update = useCallback(
    (patch, { push = false } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const resetsPage = Object.keys(patch).some((k) => k !== 's_page');
          Object.entries(patch).forEach(([key, value]) => {
            if (value === '' || value == null || (key === 's_page' && Number(value) <= 1)) next.delete(key);
            else next.set(key, String(value));
          });
          if (resetsPage && !('s_page' in patch)) next.delete('s_page');
          return next;
        },
        { replace: !push }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(filters.q);
  const debouncedSearch = useDebounce(searchInput, 400);
  useEffect(() => {
    if (debouncedSearch !== filters.q) update({ s_q: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  useEffect(() => {
    if (filters.q !== debouncedSearch) setSearchInput(filters.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // List
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  // Status totals come from the list response (status_counts) — no extra fan-out calls.
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    const params = { project_id: projectId, page, limit: pageSize };
    if (filters.q.trim().length >= MIN_SEARCH_LENGTH) params.search = filters.q.trim();
    if (filters.type) params.schedule_type = filters.type;
    if (filters.status) params.status_filter = filters.status;
    if (filters.queue) params.queue_status = filters.queue;

    setLoading(true);
    setError(null);
    axiosInstance
      .get('/test_plan/schedule_test_plan_list', { params, skipGlobalLoader: true })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        if (!response?.status) throw new Error(response?.message || 'Failed to load schedules');
        const count = Number(response.data?.total) || 0;
        const pages = Math.ceil(count / pageSize);
        setRows(Array.isArray(response.data?.schedules) ? response.data.schedules : []);
        setTotal(count);
        const sc = response.data?.status_counts;
        if (sc && typeof sc === 'object') setCounts(sc);
        if (pages > 0 && page > pages) update({ s_page: pages });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error('Error fetching schedules:', err);
        setError(err?.message || 'Failed to load schedules');
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
        setHasLoaded(true);
      });
  }, [projectId, page, pageSize, filters.q, filters.type, filters.status, filters.queue, refreshKey, update]);

  // Plans only needed for the schedule dialog — load lazily when opened.
  const [plans, setPlans] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const ensurePlans = useCallback(async () => {
    if (!projectId || plansLoaded || plansLoading) return;
    setPlansLoading(true);
    try {
      const res = await axiosInstance.get(`/test_plan/test-plans/all_type?project_id=${projectId}`, {
        skipGlobalLoader: true,
        skipGlobalToast: true,
      });
      const raw = Array.isArray(res?.test_plans) ? res.test_plans : [];
      setPlans(raw.map((p) => ({ test_plan_id: p.test_plan_id, name: p.name })));
    } catch (err) {
      console.error('Error fetching test plans:', err);
    } finally {
      setPlansLoaded(true);
      setPlansLoading(false);
    }
  }, [projectId, plansLoaded, plansLoading]);

  useEffect(() => {
    // Reset cached plans when project changes / explicit refresh.
    setPlans([]);
    setPlansLoaded(false);
  }, [projectId, refreshKey]);

  const planNameById = useMemo(
    () => new Map(plans.map((p) => [String(p.test_plan_id), p.name])),
    [plans]
  );

  // Dialogs
  const [scheduleDialog, setScheduleDialog] = useState(null); // { plan } | null
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await axiosInstance.delete(`/test_plan/${deleteTarget.id}`);
      if (response?.status) {
        toast.success('Schedule deleted');
        setDeleteTarget(null);
        refresh();
      } else {
        throw new Error(response?.message || 'Failed to delete schedule');
      }
    } catch (err) {
      console.error('Error deleting schedule:', err);
      toast.error(err?.message || 'Failed to delete schedule');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, refresh]);

  const openPlan = useCallback(
    (planId) => {
      const workspaceId = getWorkspaceIdFromURL();
      const path = `test-plans/${planId}`;
      navigate(workspaceId ? buildWorkspaceDashboardPath(workspaceId, projectId, path) : `/dashboard/${path}`);
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const openScheduleDialog = useCallback(
    async (plan = null) => {
      await ensurePlans();
      setScheduleDialog({ plan });
    },
    [ensurePlans]
  );

  return {
    projectId,
    rows,
    total,
    page,
    pageSize,
    filters,
    filtersActive,
    searchInput,
    setSearchInput,
    update,
    loading,
    initialLoading: loading && !hasLoaded,
    error,
    counts,
    plans,
    plansLoaded,
    plansLoading,
    planNameById,
    refresh,
    scheduleDialog,
    setScheduleDialog,
    openScheduleDialog,
    deleteTarget,
    setDeleteTarget,
    deleting,
    confirmDelete,
    openPlan,
  };
}

/* ------------------------------------------------------------------ */
/* View                                                                 */
/* ------------------------------------------------------------------ */

function SchedulesActions({ ctl }) {
  const noPlans = ctl.plansLoaded && ctl.plans.length === 0;
  return (
    <>
      <button
        type="button"
        className="rt-btn rt-btn--secondary"
        onClick={ctl.refresh}
        disabled={!ctl.projectId || ctl.loading}
      >
        <LuRefreshCw size={14} className={ctl.loading && !ctl.initialLoading ? 'rt-spin' : undefined} /> Refresh
      </button>
      <button
        type="button"
        className="rt-btn rt-btn--primary"
        onClick={() => ctl.openScheduleDialog(null)}
        disabled={!ctl.projectId || (ctl.plansLoaded && ctl.plans.length === 0)}
        title={noPlans ? 'Create a test plan first' : undefined}
      >
        <LuPlus size={14} /> Schedule plan
      </button>
    </>
  );
}

function SchedulesContent({ ctl }) {
  const { filters, update, counts } = ctl;

  if (!ctl.projectId) {
    return <EmptyState title="Select a project" description="Choose a project to see its scheduled runs." />;
  }

  const planLabel = (row) =>
    row.test_plan_name || ctl.planNameById.get(String(row.test_plan_id)) || null;

  const columns = [
    {
      key: 'name',
      header: 'Schedule',
      render: (row) => (
        <div className="rt-sched-name">
          <span className="rt-sched-name__title">{row.schedule_name || 'Untitled schedule'}</span>
          {row.comments && (
            <span className="rt-sched-sub" title={row.comments}>
              {row.comments}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Test plan',
      render: (row) => {
        const name = planLabel(row);
        if (!row.test_plan_id) return name || '—';
        return (
          <button
            type="button"
            className="rt2-link"
            onClick={(e) => {
              e.stopPropagation();
              ctl.openPlan(row.test_plan_id);
            }}
          >
            {name || `Plan #${row.test_plan_id}`}
          </button>
        );
      },
    },
    {
      key: 'run_time',
      header: 'Runs at',
      render: (row) => (
        <div className="rt-sched-time">
          <span>{formatDateTime(row.run_time)}</span>
          <span className="rt-sched-sub">{formatRelative(row.run_time)}</span>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Repeats',
      render: (row) => <Badge tone="muted">{humanize(row.schedule_type)}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge tone={STATUS_TONE[row.status] || 'muted'}>{humanize(row.status)}</Badge>,
    },
    {
      key: 'queue',
      header: 'Queue',
      render: (row) => <Badge tone={QUEUE_TONE[row.queue_status] || 'muted'}>{humanize(row.queue_status)}</Badge>,
    },
    {
      key: 'created',
      header: 'Created',
      render: (row) => <span className="rt-sched-sub rt-sched-sub--plain">{formatDateTime(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      render: (row) => (
        <RowActions
          menuItems={[
            ...(row.test_plan_id
              ? [
                  { id: 'open', label: 'Open test plan', onClick: () => ctl.openPlan(row.test_plan_id) },
                  {
                    id: 'again',
                    label: 'Schedule again',
                    onClick: () =>
                      ctl.openScheduleDialog({
                        test_plan_id: row.test_plan_id,
                        name: planLabel(row) || '',
                      }),
                  },
                ]
              : []),
            {
              id: 'delete',
              label: 'Delete',
              danger: true,
              dividerBefore: Boolean(row.test_plan_id),
              onClick: () => ctl.setDeleteTarget(row),
            },
          ]}
        />
      ),
    },
  ];

  const metric = (key) => (counts[key] == null ? '—' : counts[key]);
  const noSchedulesAtAll = !ctl.filtersActive && ctl.total === 0;

  return (
    <>
      <div className="rt-metric-grid">
        <MetricCard
          label="All schedules"
          value={metric('ALL')}
          tone="primary"
          icon={<LuCalendarClock size={14} />}
          onClick={filters.status ? () => update({ s_status: '' }) : undefined}
        />
        <MetricCard
          label="Scheduled"
          value={metric('SCHEDULED')}
          tone="info"
          context="waiting to run"
          onClick={() => update({ s_status: 'SCHEDULED' })}
        />
        <MetricCard
          label="Running"
          value={metric('RUNNING')}
          tone="accent"
          icon={<LuLoaderCircle size={14} />}
          onClick={() => update({ s_status: 'RUNNING' })}
        />
        <MetricCard
          label="Failed"
          value={metric('FAILED')}
          tone="danger"
          icon={<LuCircleAlert size={14} />}
          onClick={() => update({ s_status: 'FAILED' })}
        />
      </div>

      <FilterBar>
        <SearchInput
          value={ctl.searchInput}
          onChange={ctl.setSearchInput}
          placeholder="Search schedules…"
        />
        <Select
          className="rt-select--filter"
          value={filters.type}
          onChange={(e) => update({ s_type: e.target.value })}
          aria-label="Repeat filter"
        >
          <option value="">All repeats</option>
          {SCHEDULE_TYPES.map((t) => (
            <option key={t} value={t}>
              {humanize(t)}
            </option>
          ))}
        </Select>
        <Select
          className="rt-select--filter"
          value={filters.queue}
          onChange={(e) => update({ s_queue: e.target.value })}
          aria-label="Queue filter"
        >
          <option value="">All queue states</option>
          {QUEUE_STATUSES.map((q) => (
            <option key={q} value={q}>
              {humanize(q)}
            </option>
          ))}
        </Select>
        {ctl.filtersActive && (
          <button
            type="button"
            className="rt-btn rt-btn--tertiary rt-btn--sm"
            onClick={() => {
              ctl.setSearchInput('');
              update({ s_q: '', s_type: '', s_status: '', s_queue: '' });
            }}
          >
            Clear filters
          </button>
        )}
      </FilterBar>

      <div className="rt-chip-group" role="radiogroup" aria-label="Status filter">
        {[''].concat(SCHEDULE_STATUSES).map((status) => {
          const active = filters.status === status;
          return (
            <button
              key={status || 'all'}
              type="button"
              role="radio"
              aria-checked={active}
              className={`rt-chip rt-chip--${(status || 'all').toLowerCase()}${active ? ' rt-chip--active' : ''}`}
              onClick={() => update({ s_status: status })}
            >
              {status && <span className="rt-chip__dot" aria-hidden="true" />}
              {status ? humanize(status) : 'All'}
            </button>
          );
        })}
      </div>

      {ctl.error && !ctl.initialLoading ? (
        <ErrorState title="Unable to load schedules" description={ctl.error} onRetry={ctl.refresh} />
      ) : !ctl.initialLoading && noSchedulesAtAll ? (
        <EmptyState
          icon={<LuCalendarClock size={20} />}
          title="No schedules yet"
          description="Schedule a test plan to run once or on a repeating cadence."
          action={
            <button
              type="button"
              className="rt-btn rt-btn--primary rt-btn--sm"
              onClick={() => ctl.openScheduleDialog(null)}
              disabled={ctl.plansLoaded && ctl.plans.length === 0}
            >
              <LuPlus size={14} /> Schedule plan
            </button>
          }
        />
      ) : (
        <div className="rt-sched-table" aria-busy={ctl.loading}>
          <DataTable
            columns={columns}
            rows={ctl.rows}
            rowKey="id"
            loading={ctl.initialLoading}
            skeletonRows={ctl.pageSize > 10 ? 10 : ctl.pageSize}
            emptyTitle="No schedules match these filters"
            emptyDescription="Try a different status, repeat or search term."
          />
          {ctl.total > 0 && (
            <Pagination
              page={ctl.page}
              pageSize={ctl.pageSize}
              total={ctl.total}
              loading={ctl.loading}
              onPageChange={(p) => update({ s_page: p }, { push: true })}
              onPageSizeChange={(size) => update({ s_size: size === PAGE_SIZES[0] ? '' : size })}
              pageSizeOptions={PAGE_SIZES}
            />
          )}
        </div>
      )}

      {ctl.scheduleDialog && (
        <ScheduleTestPlanModal
          isOpen
          testPlanList={ctl.plans}
          initialPlan={ctl.scheduleDialog.plan}
          projectId={ctl.projectId}
          onClose={() => ctl.setScheduleDialog(null)}
          onFeedback={(message, type) => {
            if (type === 'error') {
              toast.error(message);
              return;
            }
            toast.success(message);
            ctl.refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(ctl.deleteTarget)}
        title="Delete schedule?"
        body={`"${ctl.deleteTarget?.schedule_name || `Schedule #${ctl.deleteTarget?.id}`}" will no longer run. This cannot be undone.`}
        confirmLabel={ctl.deleting ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => !ctl.deleting && ctl.setDeleteTarget(null)}
        onConfirm={ctl.confirmDelete}
      />
    </>
  );
}

/** Schedules list for embedding inside another page (e.g. the Executions "Scheduled" tab). */
export function SchedulesPanel() {
  const ctl = useSchedules();
  return (
    <div className="rt-sched-panel">
      <div className="rt-sched-panel__actions">
        <SchedulesActions ctl={ctl} />
      </div>
      <SchedulesContent ctl={ctl} />
    </div>
  );
}

const SchedulesWorkspace = () => {
  const ctl = useSchedules();
  return (
    <PageScaffold
      title="Schedules"
      description="Plan automated test runs and track their queue status."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Run' }, { label: 'Schedules' }]} />}
      actions={<SchedulesActions ctl={ctl} />}
    >
      <SchedulesContent ctl={ctl} />
    </PageScaffold>
  );
};

export default SchedulesWorkspace;
